import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const ADMIN_TOKEN_KEY = "aurays_admin_token";
const isTesting = !!import.meta.env.VITE_IS_TESTING;
const isDev = !import.meta.env.PROD;

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const adminRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const isAdmin = !!sessionStorage.getItem(ADMIN_TOKEN_KEY);

  const handleLogout = useCallback(() => {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    setAdminOpen(false);
    navigate("/");
    window.location.reload();
  }, [navigate]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  // Close admin dropdown on outside click
  useEffect(() => {
    if (!adminOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (adminRef.current && !adminRef.current.contains(e.target as Node)) {
        setAdminOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [adminOpen]);

  const navLinks = [
    { label: "Home", to: "/" },
    { label: "Events", to: "/events" },
    { label: "About", to: "/about" },
    { label: "Love Aura Test", to: "/love-aura-test" },
    { label: "Features", href: "#features" },
    { label: "FAQ", href: "#faq" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isDev || isTesting
          ? "bg-red-600/90 backdrop-blur-xl border-b border-red-400 shadow-sm"
          : scrolled
            ? "bg-white/80 backdrop-blur-xl border-b border-gray-100 shadow-sm"
            : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <img src="/icon.png" alt="Aurays Logo" className="h-8 w-auto" />
          <span
            className={`text-xl font-semibold tracking-tight ${isDev || isTesting ? "text-white" : "text-gray-900"}`}
          >
            Aurays
          </span>
        </Link>
        {isDev || isTesting ? (
          <span className="hidden md:inline-flex items-center rounded-full bg-white/20 text-white text-[10px] font-bold px-2.5 py-0.5 tracking-widest uppercase">
            Testing
          </span>
        ) : null}

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) =>
            link.to ? (
              <Link
                key={link.label}
                to={link.to}
                className={`text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                  isDev || isTesting
                    ? "text-white/70 hover:text-white"
                    : location.pathname === link.to
                      ? "text-gray-900"
                      : "text-gray-500 hover:text-gray-900"
                }`}
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.label}
                href={link.href}
                className={`text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                  isDev || isTesting ? "text-white/70 hover:text-white" : "text-gray-500 hover:text-gray-900"
                }`}
              >
                {link.label}
              </a>
            ),
          )}
        </div>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          {isAdmin ? (
            <div className="relative" ref={adminRef}>
              <button
                onClick={() => setAdminOpen((prev) => !prev)}
                className={`flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-full transition-colors cursor-pointer ${
                  isDev || isTesting
                    ? "text-white/70 hover:text-white hover:bg-white/10"
                    : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                }`}
              >
                <i className="ri-shield-user-line text-base"></i>
                Admin
                <i className={`ri-arrow-${adminOpen ? "up" : "down"}-s-line text-xs transition-transform`}></i>
              </button>
              {adminOpen ? (
                <div className="absolute top-full right-0 mt-2 w-52 bg-white rounded-2xl shadow-lg border border-gray-100 py-2 z-50">
                  <Link
                    to="/registration-ops"
                    onClick={() => setAdminOpen(false)}
                    className="block px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 cursor-pointer"
                  >
                    <i className="ri-dashboard-line mr-2 text-gray-400"></i>
                    Event Ops
                  </Link>
                  <Link
                    to="/email-queue"
                    onClick={() => setAdminOpen(false)}
                    className="block px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 cursor-pointer"
                  >
                    <i className="ri-mail-line mr-2 text-gray-400"></i>
                    Email Queue
                  </Link>
                  <Link
                    to="/event-admin"
                    onClick={() => setAdminOpen(false)}
                    className="block px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 cursor-pointer"
                  >
                    <i className="ri-calendar-event-line mr-2 text-gray-400"></i>
                    Event Management
                  </Link>
                  <Link
                    to="/checkin-scanner"
                    onClick={() => setAdminOpen(false)}
                    className="block px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 cursor-pointer"
                  >
                    <i className="ri-qr-scan-2-line mr-2 text-gray-400"></i>
                    Check-in Scanner
                  </Link>
                  <Link
                    to="/aurays-admin"
                    onClick={() => setAdminOpen(false)}
                    className="block px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 cursor-pointer"
                  >
                    <i className="ri-heart-line mr-2 text-gray-400"></i>
                    Admin Dashboard
                  </Link>
                  <div className="border-t border-gray-100 mt-1 pt-1">
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 rounded-b-xl cursor-pointer transition-colors"
                    >
                      <i className="ri-logout-box-line mr-2"></i>
                      Logout
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <Link
              to="/register"
              className="bg-gray-900 text-white text-sm font-medium px-5 py-2.5 rounded-full hover:bg-gray-700 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Join Now
            </Link>
          )}
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i
            className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-2xl ${isDev || isTesting ? "text-white" : "text-gray-900"}`}
          ></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div
          className={`md:hidden backdrop-blur-xl border-b px-6 pb-6 pt-2 flex flex-col gap-4 ${isDev || isTesting ? "bg-red-600/95 border-red-400" : "bg-white/95 border-gray-100"}`}
        >
          {isDev || isTesting ? (
            <span className="inline-flex items-center rounded-full bg-white/20 text-white text-[10px] font-bold px-2.5 py-0.5 tracking-widest uppercase w-fit">
              Testing
            </span>
          ) : null}
          {navLinks.map((link) =>
            link.to ? (
              <Link
                key={link.label}
                to={link.to}
                className={`text-base font-medium cursor-pointer ${isDev || isTesting ? "text-white/80 hover:text-white" : "text-gray-700 hover:text-gray-900"}`}
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.label}
                href={link.href}
                className={`text-base font-medium cursor-pointer ${isDev || isTesting ? "text-white/80 hover:text-white" : "text-gray-700 hover:text-gray-900"}`}
              >
                {link.label}
              </a>
            ),
          )}
          <Link
            to="/register"
            className="bg-gray-900 text-white text-sm font-medium px-5 py-3 rounded-full text-center cursor-pointer whitespace-nowrap"
          >
            Join Now
          </Link>
          {isAdmin ? (
            <div className="border-t border-gray-100 pt-4 mt-1">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">Admin</p>
              <Link
                to="/registration-ops"
                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 cursor-pointer py-1.5"
              >
                <i className="ri-dashboard-line text-gray-400"></i>
                Event Ops
              </Link>
              <Link
                to="/email-queue"
                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 cursor-pointer py-1.5"
              >
                <i className="ri-mail-line text-gray-400"></i>
                Email Queue
              </Link>
              <Link
                to="/event-admin"
                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 cursor-pointer py-1.5"
              >
                <i className="ri-calendar-event-line text-gray-400"></i>
                Event Management
              </Link>
              <Link
                to="/checkin-scanner"
                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 cursor-pointer py-1.5"
              >
                <i className="ri-qr-scan-2-line text-gray-400"></i>
                Check-in Scanner
              </Link>
              <Link
                to="/aurays-admin"
                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 cursor-pointer py-1.5"
              >
                <i className="ri-heart-line text-gray-400"></i>
                Admin Dashboard
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-sm font-semibold text-red-600 cursor-pointer py-1.5"
              >
                <i className="ri-logout-box-line"></i>
                Logout
              </button>
            </div>
          ) : null}
        </div>
      )}
    </nav>
  );
}
